import { useEffect } from 'react';

/**
 * Runs a callback when the page is restored from the back/forward cache.
 *
 * Pressing back after we sent the browser somewhere else — Stripe Checkout, the
 * billing portal — does not remount anything. The browser restores the page as it
 * was, React state and all, so any "in progress" flag set just before navigating is
 * still set: a spinner that never resolves and controls that stay disabled, with no
 * way out but a manual reload.
 *
 * `pageshow` with `persisted: true` is the signal for exactly that restore; an
 * ordinary load fires it with `persisted: false`, where state has already been reset
 * and there is nothing to do.
 *
 * @param {Function} onRestore called when the page comes back from bfcache
 */
const useBfcacheRestore = onRestore => {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handlePageShow = event => {
      if (event.persisted) {
        onRestore();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [onRestore]);
};

export default useBfcacheRestore;
